import React, { useState, useEffect, useMemo } from "react";
import {
    DraftailEditor,
    ENTITY_TYPE,
    BLOCK_TYPE,
    INLINE_STYLE,
    createEditorStateFromRaw,
} from "draftail";
import { EditorState } from "draft-js";
import {
    EntityImage,
    EntityLink,
    EntityVideo,
    EntityProfileMap,
    PROFILE_MAP_ENTITY,
    EntityLinkBlock,
} from "../admin/ContentEditor";
import { createEditorStateFromHTML, isDefined,} from '../../helpers';

import { Loader, Icon } from "semantic-ui-react";

const HeaderIcon = ({ level = 1 }) => (
    <Icon.Group className="pair">
        <Icon name="header" />
        <Icon>{level}</Icon>
    </Icon.Group>
);

/** Same block set as the admin editor so saved content always resolves in read-only mode. */
const READONLY_BLOCK_TYPES = [
    { type: BLOCK_TYPE.UNSTYLED, icon: <Icon name="paragraph" /> },
    { type: BLOCK_TYPE.HEADER_ONE, icon: <HeaderIcon level={1} /> },
    { type: BLOCK_TYPE.HEADER_TWO, icon: <HeaderIcon level={2} /> },
    { type: BLOCK_TYPE.HEADER_THREE, icon: <HeaderIcon level={3} /> },
    { type: BLOCK_TYPE.HEADER_FOUR, icon: <HeaderIcon level={4} /> },
    { type: BLOCK_TYPE.HEADER_FIVE, icon: <HeaderIcon level={5} /> },
    { type: BLOCK_TYPE.HEADER_SIX, icon: <HeaderIcon level={6} /> },
    { type: BLOCK_TYPE.UNORDERED_LIST_ITEM, icon: <Icon name="list ul" /> },
    { type: BLOCK_TYPE.ORDERED_LIST_ITEM, icon: <Icon name="list ol" /> },
    { type: BLOCK_TYPE.BLOCKQUOTE, icon: <Icon name="quote right" /> },
    { type: BLOCK_TYPE.CODE, icon: <Icon name="code" /> },
];

const READONLY_INLINE_STYLES = [
    { type: INLINE_STYLE.BOLD, icon: <Icon name="bold" /> },
    { type: INLINE_STYLE.ITALIC, icon: <Icon name="italic" /> },
    { type: INLINE_STYLE.UNDERLINE, icon: <Icon name="underline" /> },
    { type: INLINE_STYLE.STRIKETHROUGH, icon: <Icon name="strikethrough" /> },
];

const UnsupportedAtomicEntity = ({ blockProps }) => {
    const entity = blockProps.entity;
    const label = entity.getType();
    return (
        <span className="cd-unsupported-embed" style={{ color: "#999", fontSize: "0.9em" }}>
            [Inhalt kann nicht angezeigt werden: {label}]
        </span>
    );
};

function augmentReaderEntityTypes(editorState, baseEntityTypes) {
    const cs = editorState.getCurrentContent();
    const byType = new Map(baseEntityTypes.map((c) => [c.type, c]));
    const extra = [];
    cs.getBlockMap().forEach((block) => {
        if (block.getType() !== BLOCK_TYPE.ATOMIC) return;
        const ek = block.getEntityAt(0);
        if (!ek) return;
        const ent = cs.getEntity(ek);
        const tt = ent.getType();
        if (tt === ENTITY_TYPE.HORIZONTAL_RULE) return;
        const cfg = byType.get(tt);
        if (cfg && cfg.block) return;
        if (!extra.some((e) => e.type === tt)) {
            extra.push({ type: tt, block: UnsupportedAtomicEntity });
        }
    });
    return baseEntityTypes.concat(extra);
}

const parseState = (content) => {
    try {
        if (typeof content === "object" && isDefined(content) &&
            isDefined(content.blocks)) {
            return createEditorStateFromRaw(content);
        } else if (typeof content === "string") {
            return createEditorStateFromHTML(content);
        }
    } catch (e) {
        console.error("Failed parsing content", e);
    }
    return EditorState.createEmpty();
};

const RenderContent = ({ content }) => {
    const [editorState, setEditorState] = useState(null);

    const baseEntityTypes = useMemo(() => ([
        { type: ENTITY_TYPE.IMAGE, block: EntityImage },
        { type: "VIDEO", block: EntityVideo },
        { type: PROFILE_MAP_ENTITY, block: EntityProfileMap },
        {
            type: ENTITY_TYPE.LINK,
            decorator: EntityLink,
            block: EntityLinkBlock,
        },
    ]), []);

    // Derive from `content` only so read-only `onChange` does not replace this array every tick.
    const entityTypes = useMemo(() => {
        if (!isDefined(content)) return baseEntityTypes;
        const es = parseState(content);
        return augmentReaderEntityTypes(es, baseEntityTypes);
    }, [content, baseEntityTypes]);

    useEffect(() => {
        if (isDefined(content)) {
            setEditorState(parseState(content));
        }
    }, [content]);

    if (editorState === null) {
        return (
            <div style={{ position: "relative" }}>
                <Loader active />
            </div>
        );
    }

    return (
        <DraftailEditor
            editorState={editorState}
            readOnly
            topToolbar={null}
            onChange={(nextState) => setEditorState(nextState)}
            blockTypes={READONLY_BLOCK_TYPES}
            inlineStyles={READONLY_INLINE_STYLES}
            entityTypes={entityTypes}
            enableHorizontalRule
            enableLineBreak
            maxListNesting={2}
        />
    );
};

export default RenderContent;
